import { GetStaticProps } from 'next';
import { CondoleanceHomePageFieldsFragment, CondoleanceInformationFieldsFragment } from 'api/graphql-types';
import Layout from 'modules/layout/layout';
import { ModuleBuilder } from 'modules/module-builder/module-builder';
import client, { httpLink, setPreviewSecret } from 'api/apollo-client';
import { getServerPageGetCondoleanceHome } from 'api/graphql-helper';
import { initialState as settingsInitialState } from 'redux/stores/settings';
import { Head } from 'modules/head/head';
import { getStrapiParametersFromContext } from 'utils/strapi-parameters';
import HomepageHeader from 'modules/homepage-header/homepage-header';
import { ROUTES } from 'constants/routes';
import { IBasePageProps } from 'types/page';

interface ICondoleanceHomePagePageProps extends IBasePageProps {
	condoleanceHomepage?: CondoleanceHomePageFieldsFragment;
	condoleanceInformation?: CondoleanceInformationFieldsFragment;
}

const CondoleanceHomePage = (props: ICondoleanceHomePagePageProps): React.JSX.Element => {
	const modules = props.condoleanceHomepage?.modules;
	const seoModule = props?.condoleanceHomepage?.seo;

	return (
		<Layout navbar={props?.condoleanceInformation?.navbar} footer={props?.condoleanceInformation?.footer}>
			<Head seoModule={seoModule} seoParent={props?.condoleanceInformation?.seo} />
			<HomepageHeader module={props?.condoleanceHomepage?.header} />
			<ModuleBuilder modules={modules} slug={ROUTES.HOME.fullPath} />
		</Layout>
	);
};

export const getStaticProps: GetStaticProps<IBasePageProps> = async (ctx) => {
	try {
		const { locale, publicationState, draftMode } = getStrapiParametersFromContext(ctx);

		if (publicationState === 'DRAFT') {
			client.setLink(setPreviewSecret(process.env.STRAPI_PREVIEW_SECRET as string).concat(httpLink));
		}

		const condoleanceHomePageResult = await getServerPageGetCondoleanceHome(
			{
				variables: {
					locale,
					publicationState
				}
			},
			client
		);
		const { condoleanceHomepage, condoleanceInformation } = condoleanceHomePageResult.props.data;

		if (condoleanceHomepage == null) {
			return {
				notFound: true
			};
		}

		return {
			props: {
				condoleanceHomepage: condoleanceHomepage.data?.attributes,
				condoleanceInformation: condoleanceInformation?.data?.attributes,
				initialReduxState: {
					settings: {
						...settingsInitialState,
						publicationState,
						draftMode,
						contentType: 'condoleance-homepage',
						contentTypeId: condoleanceHomepage?.data?.documentId,
						showNeutralStyle: true,
						hideLanguageSwitcher: true,
						hideSearchbar: true,
						hideDropdowns: true,
						isSubsite: true
					}
				}
			},
			revalidate: Number(process.env.NEXT_PUBLIC_REVALIDATE_TIME_IN_SECONDS)
		};
	} catch (error: any) {
		// eslint-disable-next-line no-console
		console.error('error in CondoleanceHomePage', error);
		return {
			notFound: true
		};
	}
};

export default CondoleanceHomePage;
